import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, Clock, AlertTriangle, Shield, Users, GitBranch, Gauge } from 'lucide-react';

const GovernanceDashboard = ({ calculationId, isActive = true }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [governanceData, setGovernanceData] = useState({
    overview: {},
    pendingApprovals: [],
    versionHistory: [],
    analytics: {},
    loading: true,
    error: null
  });

  const API = require('../services/apiBase').default || require('../services/apiBase');

  useEffect(() => {
    // Only load governance data if component is active and calculationId exists
    if (isActive && calculationId) {
      loadGovernanceData();
    }
  }, [calculationId, isActive]);

  const loadGovernanceData = async () => {
    // Don't load if not active or no calculation
    if (!isActive || !calculationId) {
      return;
    }
    
    try {
      setGovernanceData(prev => ({ ...prev, loading: true, error: null }));

      const endpoints = [
        '/api/governance/health',
        '/api/governance/pending-approvals',
        '/api/governance/analytics/overview',
        '/api/governance/audit-log?days=7'
      ];

      const responses = await Promise.all(
        endpoints.map(endpoint => 
          fetch(`${API}${endpoint}`)
            .then(async res => {
              if (!res.ok) {
                // If response is not ok, return empty data instead of throwing
                console.warn(`Governance API warning: ${endpoint} returned ${res.status}`);
                return { status: 'error', error: `API returned ${res.status}` };
              }
              return await res.json();
            })
            .catch(err => {
              console.warn(`Governance API error: ${endpoint}`, err);
              return { status: 'error', error: err.message };
            })
        )
      );

      const [healthData, pendingData, analyticsData, auditData] = responses;

      // Only set data if responses are successful
      setGovernanceData({
        overview: healthData?.status === 'error' ? {} : (healthData || {}),
        pendingApprovals: pendingData?.status === 'error' ? [] : (pendingData?.pending_approvals || []),
        analytics: analyticsData?.status === 'error' ? {} : (analyticsData?.analytics || {}),
        auditLog: auditData?.status === 'error' ? [] : (auditData?.audit_log?.entries || []),
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Error loading governance data:', error);
      setGovernanceData(prev => ({
        ...prev,
        loading: false,
        error: `Failed to load governance data. This won't affect other application features.`
      }));
    }
  };

  const handleApprovalAction = async (requestId, decision, comments = '') => {
    try {
      const response = await fetch(`${API}/api/governance/approvals/${requestId}/decision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision: decision,
          comments: comments
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        // Reload data to reflect changes
        await loadGovernanceData();
        alert(`Rule ${decision}d successfully!`);
      } else {
        throw new Error(result.message || 'Approval action failed');
      }

    } catch (error) {
      console.error('Error processing approval:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Pending Approvals"
          value={governanceData.pendingApprovals.length}
          icon={Clock}
          color="orange"
        />
        <MetricCard
          title="Active Rules"
          value={governanceData.analytics?.governance_metrics?.rules_under_governance || 0}
          icon={Shield}
          color="blue"
        />
        <MetricCard
          title="Active Users"
          value={governanceData.analytics?.governance_metrics?.active_users || 0}
          icon={Users}
          color="green"
        />
        <MetricCard
          title="Version Control"
          value={governanceData.analytics?.governance_metrics?.rule_versions_created || 0}
          icon={GitBranch}
          color="blue"
        />
      </div>

      {/* System Status */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Gauge className="w-5 h-5" />
            System Status
          </h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {governanceData.overview?.services && Object.entries(governanceData.overview.services).map(([service, status]) => (
              <div key={service} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  status === 'loaded' || status === true ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="text-sm capitalize">{service.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Recent Activity</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {governanceData.auditLog?.slice(0, 5).map((entry, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div>
                  <span className="font-medium capitalize">{entry.event_type?.replace('_', ' ')}</span>
                  <span className="text-gray-500 ml-2">
                    {entry.details?.rule_name && `- ${entry.details.rule_name}`}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {new Date(entry.timestamp).toLocaleDateString()}
                </span>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ApprovalsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Pending Approvals</h3>
        <Badge variant="outline">
          {governanceData.pendingApprovals.length} pending
        </Badge>
      </div>

      {governanceData.pendingApprovals.length > 0 ? (
        <div className="space-y-4">
          {governanceData.pendingApprovals.map((request) => (
            <ApprovalCard
              key={request.id}
              request={request}
              onAction={handleApprovalAction}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-600">No pending approvals</p>
            <p className="text-sm text-gray-500">All rules have been reviewed</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const AnalyticsTab = () => {
    const metrics = governanceData.analytics;
    
    return (
      <div className="space-y-6">
        {/* Governance Metrics */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Governance Metrics</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Completed Approvals"
                value={metrics?.governance_metrics?.completed_approvals || 0}
              />
              <StatCard
                label="Versions Created"
                value={metrics?.governance_metrics?.rule_versions_created || 0}
              />
              <StatCard
                label="Rollbacks"
                value={metrics?.governance_metrics?.rollbacks_performed || 0}
              />
            </div>
          </CardContent>
        </Card>

        {/* Confidence Metrics */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">AI Confidence Metrics</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Auto Applied"
                value={metrics?.confidence_metrics?.rules_auto_applied || 0}
              />
              <StatCard
                label="Need Review"
                value={metrics?.confidence_metrics?.rules_requiring_review || 0}
              />
              <StatCard
                label="Avg Confidence"
                value={`${((metrics?.confidence_metrics?.average_confidence || 0.85) * 100).toFixed(1)}%`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Security Metrics */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Security & Access</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard
                label="Role Changes"
                value={metrics?.security_metrics?.role_changes || 0}
              />
              <StatCard
                label="Admin Actions"
                value={metrics?.security_metrics?.admin_actions || 0}
              />
              <StatCard
                label="Failed Checks"
                value={metrics?.security_metrics?.failed_permission_checks || 0}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (governanceData.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading governance dashboard...</p>
        </div>
      </div>
    );
  }

  if (governanceData.error) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {governanceData.error}
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-4" 
            onClick={loadGovernanceData}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Governance Dashboard</h2>
          <p className="text-gray-600">Phase 3D: Rule governance, versioning, and enterprise safety</p>
        </div>
        <Button variant="outline" onClick={loadGovernanceData}>
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals
            {governanceData.pendingApprovals.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {governanceData.pendingApprovals.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="approvals">
          <ApprovalsTab />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const MetricCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    orange: 'text-orange-600 bg-orange-100',
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100'
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center">
          <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatCard = ({ label, value }) => (
  <div className="text-center">
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    <div className="text-sm text-gray-600">{label}</div>
  </div>
);

const ApprovalCard = ({ request, onAction }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [comment, setComment] = useState('');

  const priorityColors = {
    1: 'bg-gray-100 text-gray-600',
    2: 'bg-blue-100 text-blue-600',
    3: 'bg-yellow-100 text-yellow-600',
    4: 'bg-orange-100 text-orange-600',
    5: 'bg-red-100 text-red-600'
  };

  const isUrgent = request.time_remaining?.urgent || false;

  return (
    <Card className={`${isUrgent ? 'border-orange-200 bg-orange-50' : ''}`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold">{request.rule_summary?.rule_name || 'Unnamed Rule'}</h4>
              <Badge variant="outline" className={priorityColors[request.priority] || 'bg-gray-100'}>
                {request.priority_label || 'Normal'}
              </Badge>
              {request.rule_summary?.ai_generated && (
                <Badge variant="secondary">AI Generated</Badge>
              )}
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>Submitted by: {request.submitted_by_name}</span>
              <span>Level: {request.approval_level}</span>
              <span>Scope: {request.rule_summary?.scope || 'Unknown'}</span>
            </div>
            
            {request.rule_summary?.ai_confidence && (
              <div className="mt-2">
                <span className="text-sm text-gray-600">
                  AI Confidence: {(request.rule_summary.ai_confidence * 100).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          
          <div className="text-right">
            <div className="text-sm text-gray-600 mb-1">
              {request.time_remaining?.expired ? (
                <span className="text-red-600 font-medium">Expired</span>
              ) : (
                <span className={isUrgent ? 'text-orange-600 font-medium' : ''}>
                  {request.time_remaining?.hours_remaining}h remaining
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-700 mb-2">
            {request.routing_reason}
          </p>
          
          {request.rule_summary?.estimated_impact > 0 && (
            <p className="text-sm text-gray-600">
              Estimated impact: ~{request.rule_summary.estimated_impact} accounts
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide' : 'Show'} Details
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={() => onAction(request.id, 'approve', comment)}
            disabled={request.time_remaining?.expired}
          >
            Approve
          </Button>
          
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onAction(request.id, 'reject', comment)}
            disabled={request.time_remaining?.expired}
          >
            Reject
          </Button>
        </div>

        {showDetails && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <div>
              <h5 className="font-medium mb-2">Rule Details</h5>
              <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto">
                {JSON.stringify(request.rule, null, 2)}
              </pre>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Comments (optional)</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                rows="3"
                placeholder="Add comments about your decision..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GovernanceDashboard;